import { useEffect, useRef, type MouseEvent } from 'react'

export type HotkeyCode = 'Space' | 'KeyL' | 'KeyR'
export type HotkeyHandlers = Partial<Record<HotkeyCode, () => void>>

const TEXT_ENTRY_SELECTOR = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])'
const NATIVE_SPACE_SELECTOR = 'button, summary, [role="button"]'

/**
 * Binds tool hotkeys by physical key, so L and R work on the Russian layout too.
 * Typing in a field and browser shortcuts stay untouched, and Space on a focused
 * button keeps its native activation.
 */
export function useHotkeys(handlers: HotkeyHandlers): void {
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return

      const target = event.target instanceof Element ? event.target : null
      if (target?.closest(TEXT_ENTRY_SELECTOR)) return
      if (event.code === 'Space' && target?.closest(NATIVE_SPACE_SELECTOR)) return

      const handler = handlersRef.current[event.code as HotkeyCode]
      if (!handler) return

      event.preventDefault()
      handler()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])
}

/**
 * Drops focus after a mouse or touch press, so the next Space goes to the hotkey
 * instead of repeating the button. Keyboard presses have `detail === 0` and keep focus.
 */
export function releasePointerFocus(event: MouseEvent<HTMLElement>): void {
  if (event.detail > 0) event.currentTarget.blur()
}
